import {
  CLIENT_HEADER,
  ERROR_CODES,
  GARAGE_EXPECTED_HEADER as SHARED_GARAGE_EXPECTED_HEADER,
  PORTAL_TOKEN_HEADER,
  buildPath,
  routes,
  type RouteDef,
  type RouteInput,
  type RouteKey,
  type RouteOutput,
} from '@garage/shared';
import { reportAccessDenied, reportApiIssue } from './session-issues.js';

/**
 * S35 : garage attendu par l'onglet (slug de l'adresse `/g/<slug>/`). Envoyé en en-tête sur chaque
 * requête de l'espace garage ; le serveur répond 409 `garage_mismatch` si la session est passée sur un
 * autre garage (autre onglet). Ce n'est jamais la source du `garage_id` : seulement un contrôle.
 */
export const GARAGE_EXPECTED_HEADER = SHARED_GARAGE_EXPECTED_HEADER;
export const GARAGE_MISMATCH = ERROR_CODES.garageMismatch;

let expectedGarage: string | null = null;

/** Posé par l'espace garage (`GarageSpace`) ; `null` hors de l'espace garage. */
export function setExpectedGarage(slug: string | null): void {
  expectedGarage = slug;
}

export function getExpectedGarage(): string | null {
  return expectedGarage;
}

/**
 * Client API typé, dérivé du contrat `@garage/shared/routes` : méthode, chemin et schémas viennent
 * du contrat ; la réponse est validée par son schéma. Appels relatifs (`/api/...`) : aucun domaine
 * n'est figé dans le front.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

let portalToken: string | null = null;

/** Jeton du portail client, lu dans le fragment de l'URL (#…) : jamais envoyé dans une URL. */
export function setPortalToken(token: string | null): void {
  portalToken = token;
}

/** Routes d'envoi de fichier : le corps est un FormData (fichier + champs texte). */
type MultipartKey = { [K in RouteKey]: (typeof routes)[K] extends { multipart: true } ? K : never }[RouteKey];

type Input<K extends RouteKey> = {
  [P in keyof RouteInput<K> as RouteInput<K>[P] extends undefined
    ? K extends MultipartKey
      ? P extends 'body'
        ? P
        : never
      : never
    : P]: K extends MultipartKey ? (P extends 'body' ? FormData : RouteInput<K>[P]) : RouteInput<K>[P];
};

export async function api<K extends RouteKey>(key: K, input?: Input<K>): Promise<RouteOutput<K>> {
  const def: RouteDef = routes[key];
  const { params, query, body } = (input ?? {}) as {
    params?: Record<string, string>;
    query?: Record<string, string | number | readonly string[] | undefined>;
    body?: unknown;
  };
  let url = buildPath(def.path, params);
  if (query) {
    // Tableaux : clé répétée (`?make=Porsche&make=Alpine`), lue en tableau par l'API (vague 4).
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === '') continue;
      if (Array.isArray(v)) for (const item of v) search.append(k, String(item));
      else search.append(k, String(v));
    }
    const qs = search.toString();
    if (qs) url += `?${qs}`;
  }
  const headers: Record<string, string> = { [CLIENT_HEADER]: 'web', accept: 'application/json' };
  if (def.space === 'portal' && portalToken) headers[PORTAL_TOKEN_HEADER] = portalToken;
  if (def.space === 'garage' && expectedGarage) headers[GARAGE_EXPECTED_HEADER] = expectedGarage;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const res = await fetch(url, { method: def.method, headers, body: payload, credentials: 'same-origin' });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string; details?: unknown } } | null;
    const apiError = new ApiError(res.status, err?.error?.code ?? 'http_error', err?.error?.message ?? res.statusText, err?.error?.details);
    // Vague 5 : garage suspendu ou accès support terminé, signalés à l'interface quelle que soit la requête.
    if (res.status === 403 || (res.status === 409 && apiError.code === GARAGE_MISMATCH)) reportApiIssue(apiError.code, apiError.message);
    // Accès refusé dans l'espace garage (membre retiré, accès support terminé sous l'ancien code
    // `forbidden`) : l'espace garage relit la session pour en tirer la conséquence.
    if (def.space === 'garage' && (res.status === 401 || res.status === 403)) reportAccessDenied(apiError.code);
    throw apiError;
  }
  if (!def.response || res.status === 204) return null as RouteOutput<K>;
  const raw: unknown = await res.json();
  return def.response.parse(raw) as RouteOutput<K>;
}

/** URL d'un fichier servi par l'API (photo), à utiliser dans <img src>. */
export function fileUrl(key: 'media.file' | 'portal.mediaFile', id: string, variant: 'thumb' | 'display' | 'original' = 'display') {
  return `${buildPath(routes[key].path, { id })}?variant=${variant}`;
}
